import { useProvinces, useDistricts, useSubdistricts, normalizeProvince } from '@/services/addressService'
import SearchableSelect from '@/components/ui/SearchableSelect'
import { type InvoiceInfo, type RecipientType, emptyInvoiceInfo } from '@/types/invoiceInfo'
import { filterTaxId } from '@/lib/inputFilters'

// ใช้ร่วมกัน 2 จุด: checkout ซื้อคอร์ส/onsite (EnrollCourse) และตอนยอมรับใบเสนอราคา In-house (InhouseRequest)
const inputClass =
  'w-full rounded-sm border border-border bg-surface-2 px-3.5 py-2.5 text-sm text-text placeholder:text-text-muted/70 outline-none transition focus:border-indigo-400'

// เลือกจังหวัด → เขต/อำเภอ → แขวง/ตำบล แบบ dropdown ไล่ระดับ (ห้ามพิมพ์เอง) รหัสไปรษณีย์เติมอัตโนมัติจากแขวง/ตำบลที่เลือก
// เพราะ 1 แขวง/ตำบลผูกกับรหัสไปรษณีย์เดียวเสมอในข้อมูลจริง — ไม่ต้องให้ผู้ใช้กรอกเอง กันพิมพ์ผิด/ไม่ตรงกัน
export function AddressFields({ info, onChange, subdistrictColSpan }: {
  info: InvoiceInfo
  onChange: (next: InvoiceInfo) => void
  subdistrictColSpan?: boolean
}) {
  const province = normalizeProvince(info.province)
  const { data: provinces, loading: provincesLoading, error: provincesError } = useProvinces()
  const { data: districts, loading: districtsLoading } = useDistricts(province)
  const { data: subdistricts, loading: subdistrictsLoading } = useSubdistricts(province, info.district)

  return (
    <>
      <div>
        <label className="mb-1.5 block text-sm text-text-muted">จังหวัด</label>
        <SearchableSelect
          className={inputClass} value={province} options={provinces} loading={provincesLoading}
          placeholder="เลือกจังหวัด" searchPlaceholder="ค้นหาจังหวัด..."
          onChange={(v) => onChange({ ...info, province: v, district: '', subdistrict: '', postalCode: '' })}
        />
        {provincesError && <p className="mt-1 text-xs text-rose-500">โหลดรายชื่อจังหวัดไม่สำเร็จ กรุณารีเฟรชหน้านี้</p>}
      </div>
      <div>
        <label className="mb-1.5 block text-sm text-text-muted">เขต/อำเภอ</label>
        <SearchableSelect
          className={inputClass} value={info.district} options={districts} disabled={!info.province} loading={!!info.province && districtsLoading}
          placeholder={!info.province ? 'เลือกจังหวัดก่อน' : 'เลือกเขต/อำเภอ'} searchPlaceholder="ค้นหาเขต/อำเภอ..."
          onChange={(v) => onChange({ ...info, district: v, subdistrict: '', postalCode: '' })}
        />
      </div>
      <div className={subdistrictColSpan ? 'sm:col-span-2' : ''}>
        <label className="mb-1.5 block text-sm text-text-muted">แขวง/ตำบล</label>
        <SearchableSelect
          className={inputClass} value={info.subdistrict} options={subdistricts.map((s) => s.name)}
          disabled={!info.district} loading={!!info.district && subdistrictsLoading}
          placeholder={!info.district ? 'เลือกเขต/อำเภอก่อน' : 'เลือกแขวง/ตำบล'} searchPlaceholder="ค้นหาแขวง/ตำบล..."
          onChange={(v) => {
            const zip = subdistricts.find((s) => s.name === v)?.zip
            onChange({ ...info, subdistrict: v, postalCode: zip ? String(zip) : '' })
          }}
        />
      </div>
      <div>
        <label className="mb-1.5 block text-sm text-text-muted">รหัสไปรษณีย์</label>
        <input className={`${inputClass} bg-surface`} value={info.postalCode} readOnly placeholder="เลือกแขวง/ตำบลก่อน" />
      </div>
    </>
  )
}

// ── ข้อมูลผู้สั่งซื้อ (สำหรับใบกำกับภาษี/ใบเสร็จ/ใบแจ้งหนี้) ──────────────────────
// บัญชีบริษัท: ล็อกเป็นนิติบุคคล/บริษัทเสมอ ต้องกรอกครบ ไม่มีตัวเลือกปิด (alwaysOptional=false — ค่าเริ่มต้น ใช้กับซื้อคอร์ส)
// บัญชีบุคคลทั่วไป หรือ alwaysOptional=true (ใบแจ้งหนี้ In-house): เลือกได้ว่าจะรับเอกสารในนามบุคคลทั่วไป
// (เลือกได้ว่าต้องการเอกสารไหม) หรือนิติบุคคล/บริษัท
// companyOnly (ใบแจ้งหนี้ In-house — อัปเดต 2026-09-30): คำขอ In-house เป็นของบริษัทเกือบทั้งหมดในทางธุรกิจ
// จึงล็อกเป็นนิติบุคคล/บริษัทเสมอ ไม่มีตัวเลือกบุคคลทั่วไปให้เลือกเลย (ต่างจาก alwaysOptional เฉยๆ ที่ยังเลือกได้)
export default function InvoiceInfoForm({ isCompany, info, onChange, alwaysOptional, companyOnly, title, subtitle }: {
  isCompany: boolean
  info: InvoiceInfo
  onChange: (next: InvoiceInfo) => void
  alwaysOptional?: boolean
  companyOnly?: boolean
  title?: string
  subtitle?: string
}) {
  const set = <K extends keyof InvoiceInfo>(key: K, value: InvoiceInfo[K]) => onChange({ ...info, [key]: value })
  const isCompanyRecipient = companyOnly || info.recipientType === 'COMPANY'
  const forceRequired = isCompany && !alwaysOptional
  const showChoice = !forceRequired && !companyOnly
  const showFields = forceRequired || companyOnly || isCompanyRecipient || info.wantsInvoice

  return (
    <div className="rounded-sm border border-border bg-surface p-6 shadow-sm">
      <h2 className="mb-1 border-l-4 border-indigo-500 pl-3 text-xl font-semibold text-text">{title ?? 'ข้อมูลผู้สั่งซื้อ'}</h2>
      <p className="mb-5 pl-3 text-xs text-text-muted">{subtitle ?? 'สำหรับออกใบกำกับภาษี/ใบเสร็จรับเงิน'}</p>

      {showChoice && (
        <div className="mb-4 flex gap-3">
          {(['INDIVIDUAL', 'COMPANY'] as RecipientType[]).map((t) => (
            <label
              key={t}
              className={`flex flex-1 cursor-pointer items-center justify-center gap-2 rounded-sm border-2 py-3 text-sm font-medium transition-colors ${
                info.recipientType === t ? 'border-indigo-400 bg-indigo-500/10 text-text' : 'border-border text-text-muted hover:border-accent/30'
              }`}
            >
              <input
                type="radio" name="recipientType" className="accent-indigo-500"
                checked={info.recipientType === t}
                onChange={() => onChange({ ...emptyInvoiceInfo(t, info.wantsInvoice), wantsInvoice: info.wantsInvoice })}
              />
              {t === 'INDIVIDUAL' ? 'บุคคลทั่วไป' : 'นิติบุคคล/บริษัท'}
            </label>
          ))}
        </div>
      )}

      {showChoice && info.recipientType === 'INDIVIDUAL' && (
        <label className="mb-4 flex cursor-pointer items-center gap-2.5 text-sm text-text">
          <input
            type="checkbox" className="h-4 w-4 accent-indigo-500"
            checked={info.wantsInvoice}
            onChange={(e) => set('wantsInvoice', e.target.checked)}
          />
          ต้องการออกใบกำกับภาษี/ใบเสร็จรับเงิน
        </label>
      )}

      {showFields && (
        isCompanyRecipient ? (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <label className="mb-1.5 block text-sm text-text-muted">ชื่อบริษัท</label>
              <input className={inputClass} value={info.companyName} onChange={(e) => set('companyName', e.target.value)} placeholder="บริษัท ... จำกัด" />
            </div>
            <div>
              <label className="mb-1.5 block text-sm text-text-muted">เลขประจำตัวผู้เสียภาษี</label>
              <input className={inputClass} value={info.taxId} onChange={(e) => set('taxId', filterTaxId(e.target.value))} inputMode="numeric" maxLength={13} placeholder="13 หลัก" />
            </div>
            <div>
              <label className="mb-1.5 block text-sm text-text-muted">รหัสสาขา</label>
              <input className={inputClass} value={info.branchCode} onChange={(e) => set('branchCode', e.target.value)} placeholder="00000 (สำนักงานใหญ่)" />
            </div>
            <div className="sm:col-span-2">
              <label className="mb-1.5 block text-sm text-text-muted">ที่อยู่</label>
              <input className={inputClass} value={info.address} onChange={(e) => set('address', e.target.value)} placeholder="เลขที่ ถนน ซอย" />
            </div>
            <AddressFields info={info} onChange={onChange} />
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <label className="mb-1.5 block text-sm text-text-muted">ชื่อ-นามสกุล</label>
              <input className={inputClass} value={info.fullName} onChange={(e) => set('fullName', e.target.value)} />
            </div>
            <div>
              <label className="mb-1.5 block text-sm text-text-muted">เลขประจำตัวผู้เสียภาษี</label>
              <input className={inputClass} value={info.taxId} onChange={(e) => set('taxId', filterTaxId(e.target.value))} inputMode="numeric" maxLength={13} placeholder="13 หลัก" />
            </div>
            <div className="sm:col-span-2">
              <label className="mb-1.5 block text-sm text-text-muted">ที่อยู่</label>
              <input className={inputClass} value={info.address} onChange={(e) => set('address', e.target.value)} placeholder="เลขที่ ถนน ซอย" />
            </div>
            <AddressFields info={info} onChange={onChange} subdistrictColSpan />
          </div>
        )
      )}
    </div>
  )
}
